// Configuration types for MockDetector scanning

export type ProjectSize = 'small' | 'medium' | 'large';
export type DatabaseStrategy = 'all' | 'prioritized' | 'custom';

export interface RouteScanConfig {
  maxRoutes: number;           // Max routes per scan
  timeoutPerRoute: number;     // Timeout per route (ms)
  totalTimeout: number;        // Total timeout (ms)
  batchSize: number;           // Routes per batch
  enableProgressSave: boolean; // Save progress between batches
}

export interface DatabaseScanConfig {
  rowsPerTable: number;
  timeout: number;
  prioritizeTables?: string[];
  onlyPrioritizedTables?: boolean;
  showWarning?: boolean;
  intelligentSampling: boolean; // First 100, last 100, random 100
}

export interface SourceFileScanConfig {
  rootPath: string;
  includeEdgeFunctions: boolean;
  includeConfigFiles: boolean;
  fileTypes: string[];
  maxFiles: number;
  batchSize: number;
  timeout: number;
}

export interface ScanLimits {
  routes: RouteScanConfig;
  database: DatabaseScanConfig;
  sourceFiles: SourceFileScanConfig;
}

export interface ProjectSizeMetrics {
  routeCount: number;
  databaseSize: 'small' | 'medium' | 'large';
  estimatedSourceFiles: number;
  score: number; // 0-6 points
  size: ProjectSize;
}

// Default configurations for different project sizes
export const DEFAULT_SCAN_CONFIGS: Record<ProjectSize, ScanLimits> = {
  small: {
    routes: {
      maxRoutes: 100,
      timeoutPerRoute: 3000,
      totalTimeout: 300000, // 5 minutes
      batchSize: 20,
      enableProgressSave: false
    },
    database: {
      rowsPerTable: 200,
      timeout: 60000, // 1 minute
      intelligentSampling: false
    },
    sourceFiles: {
      rootPath: '/workspace/src',
      includeEdgeFunctions: true,
      includeConfigFiles: true,
      fileTypes: ['.tsx', '.ts', '.jsx', '.js'],
      maxFiles: 2000,
      batchSize: 500,
      timeout: 120000 // 2 minutes
    }
  },
  medium: {
    routes: {
      maxRoutes: 50,
      timeoutPerRoute: 3000,
      totalTimeout: 180000, // 3 minutes
      batchSize: 10,
      enableProgressSave: true
    },
    database: {
      rowsPerTable: 100,
      timeout: 120000, // 2 minutes
      prioritizeTables: ['users', 'products', 'orders', 'ai_hotel_rooms', 'profiles'],
      intelligentSampling: true
    },
    sourceFiles: {
      rootPath: '/workspace/src',
      includeEdgeFunctions: true,
      includeConfigFiles: false,
      fileTypes: ['.tsx', '.ts', '.jsx', '.js'],
      maxFiles: 1000,
      batchSize: 200,
      timeout: 180000 // 3 minutes
    }
  },
  large: {
    routes: {
      maxRoutes: 30,
      timeoutPerRoute: 2000,
      totalTimeout: 120000, // 2 minutes
      batchSize: 5,
      enableProgressSave: true
    },
    database: {
      rowsPerTable: 50,
      timeout: 180000, // 3 minutes
      prioritizeTables: ['users', 'products', 'orders'],
      onlyPrioritizedTables: true,
      showWarning: true,
      intelligentSampling: true
    },
    sourceFiles: {
      rootPath: '/workspace/src',
      includeEdgeFunctions: false,
      includeConfigFiles: false,
      fileTypes: ['.tsx', '.ts'],
      maxFiles: 500,
      batchSize: 100,
      timeout: 180000 // 3 minutes
    }
  }
};
